<template>
  <div>
    <el-row>
      <el-col :md="12">
        <el-form-item :label="$t('대표자명')" :error="formError.ceoNm" required>
          <el-input
            v-model="inputForm.ceoNm"
            maxlength="20"
            clearable
            show-word-limit
            :disabled="locked('ceoNm')"></el-input>
        </el-form-item>
      </el-col>
      <el-col :md="12">
        <el-form-item :label="$t('상호명')" :error="formError.busiNm" required>
          <el-input
            v-model="inputForm.busiNm"
            maxlength="50"
            clearable
            show-word-limit
            :disabled="locked('busiNm')"></el-input>
        </el-form-item>
      </el-col>
      <el-col :md="12">
        <el-form-item :label="$t('사업자 등록 번호')" :error="formError.busiNum" required>
          <el-input
            v-keep-number-caret
            :value="util.bizNoFieldText(inputForm.busiNum)"
            maxlength="20"
            clearable
            show-word-limit
            :disabled="locked('busiNum')"
            @input="inputForm.busiNum = util.bizNoInputValue($event)"></el-input>
        </el-form-item>
      </el-col>
      <ceo-verification-line :block="inputForm.ceoVerification"></ceo-verification-line>
    </el-row>

    <el-row v-if="isInternalAdmin">
      <el-col :md="12">
        <el-form-item :label="$t('업태')" :error="formError.busiTp1">
          <el-input
            v-model="inputForm.busiTp1"
            maxlength="100"
            clearable
            show-word-limit
            :disabled="locked('busiTp1')"></el-input>
        </el-form-item>
      </el-col>
      <el-col :md="12">
        <el-form-item :label="$t('종목')" :error="formError.busiTp2">
          <el-input
            v-model="inputForm.busiTp2"
            maxlength="100"
            clearable
            show-word-limit
            :disabled="locked('busiTp2')"></el-input>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row>
      <el-col :md="12">
        <el-form-item :label="$t('우편 번호')" :error="formError.zipCd" required>
          <el-input
            v-model="inputForm.zipCd"
            maxlength="10"
            show-word-limit
            style="width: 200px"
            readonly
            :disabled="locked('zipCd')"></el-input>
          <el-button :disabled="locked('zipCd') || locked('addr')" @click="selectAddress()">{{
            $t('주소 조회')
          }}</el-button>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row>
      <el-col :md="12">
        <el-form-item :label="$t('사업자 주소')" :error="formError.addr" required>
          <el-input
            v-model="inputForm.addr"
            maxlength="250"
            clearable
            show-word-limit
            readonly
            :disabled="locked('addr')"></el-input>
        </el-form-item>
      </el-col>
      <el-col :md="12">
        <el-form-item :label="$t('사업자 상세 주소')" :error="formError.addrDtl" required>
          <el-input
            ref="addr_dtl"
            v-model="inputForm.addrDtl"
            maxlength="50"
            clearable
            show-word-limit
            :disabled="locked('addrDtl')"></el-input>
        </el-form-item>
      </el-col>
      <el-col :md="12">
        <el-form-item :label="$t('이메일')" :error="formError.busiEmail">
          <el-input
            v-model="inputForm.busiEmail"
            maxlength="50"
            clearable
            show-word-limit
            :disabled="locked('busiEmail')"></el-input>
        </el-form-item>
      </el-col>

      <el-col v-if="isInternalAdmin" :md="12">
        <el-form-item :label="$t('팩스번호')" :error="formError.fax">
          <el-input
            v-keep-number-caret
            :value="util.telFieldText(inputForm.fax)"
            maxlength="20"
            clearable
            show-word-limit
            :disabled="locked('fax')"
            @input="inputForm.fax = util.telDigits($event)"></el-input>
        </el-form-item>
      </el-col>
      <el-col v-if="canEditSettlementAccount" :md="12">
        <el-form-item :label="$t('정산은행')" :error="formError['bankTp.cd']">
          <code-combo
            :data="bankTp"
            :model="inputForm.bankTp"
            :disabled="locked('bankTp')"
            @change="inputForm.bankTp = $event"></code-combo>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row>
      <el-col v-if="canEditSettlementAccount && !withheld('accountNumber')" :md="12">
        <el-form-item :label="$t('정산계좌번호')" :error="formError.accountNumber">
          <el-input
            v-model="inputForm.accountNumber"
            maxlength="20"
            clearable
            show-word-limit
            :disabled="locked('accountNumber')"
            @keyup.native="inputForm.accountNumber = util.inputBusiNumNew($event)"></el-input>
        </el-form-item>
      </el-col>
      <el-col v-if="canEditSettlementAccount" :md="12">
        <el-form-item :label="$t('예금주명')" :error="formError.accountName">
          <el-input
            v-model="inputForm.accountName"
            maxlength="20"
            clearable
            show-word-limit
            :disabled="locked('accountName')"></el-input>
        </el-form-item>
      </el-col>

      <!-- PG사를 바꾸면 그 PG사의 저장값을 채운다 — v-model 이 아니다: 저장하지 않은 입력이 있으면 확인 뒤에 바꾼다(`choosePgKind`) -->
      <el-col :md="12">
        <el-form-item label="PG사" :error="formError.pgKind">
          <el-select :value="pgKind" :disabled="locked('pgKind')" @change="choosePgKind">
            <el-option
              v-for="option in pgKindOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"></el-option>
          </el-select>
        </el-form-item>
      </el-col>

      <!-- 미사용이면 두 칸은 잠그고 비워 그리기만 한다 — 폼 값은 그대로 보낸다(`pgMid`·`pgMerchantKey`) -->
      <el-col :md="12">
        <el-form-item label="MID" :error="formError.mid">
          <el-input
            v-model="pgMid"
            maxlength="50"
            clearable
            show-word-limit
            :disabled="locked('mid') || pgUnused"></el-input>
        </el-form-item>
      </el-col>
      <!-- PG 키·취소 비밀번호는 대행 계정의 상세에 **키째 없다** — 값을 모르는 칸은 자리째 뺀다(`utils/storeOps`) -->
      <el-col v-if="!withheld('merchantKey')" :md="12">
        <el-form-item label="라이센스 키" :error="formError.merchantKey">
          <el-input
            v-model="pgMerchantKey"
            maxlength="100"
            clearable
            show-word-limit
            :disabled="locked('merchantKey') || pgUnused"></el-input>
        </el-form-item>
      </el-col>
      <el-col v-if="!withheld('cancelPw')" :md="12">
        <el-form-item label="취소 비밀번호" :error="formError.cancelPw">
          <el-input
            v-model="inputForm.cancelPw"
            type="password"
            maxlength="50"
            clearable
            show-word-limit
            :disabled="locked('cancelPw')"></el-input>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row v-if="isInternalAdmin" :gutter="10">
      <el-col :md="8">
        <el-form-item :label="$t('첨부 파일')">
          <el-upload
            action=""
            class="avatar-uploader"
            accept="image/*"
            :auto-upload="false"
            :file-list="inputForm.files"
            :on-change="storeFileAdd"
            :on-remove="storeFileRemove"
            :disabled="locked('files')"
            multiple
            list-type="picture">
            <el-button slot="trigger" size="small" type="primary">{{ $t('파일 추가') }}</el-button>
          </el-upload>
        </el-form-item>
      </el-col>
      <el-col :md="8">
        <div style="margin-top: 30px">&nbsp;</div>
        <div
          v-for="(item, index) in inputForm.files"
          :key="index"
          class="file_name"
          style="margin-top: 10px; height: 92px"
          v-if="'del' != inputForm.files[index].status">
          <el-input
            v-model="inputForm.files[index].name"
            maxlength="50"
            show-word-limit
            :size="'small'"
            type="textarea"
            resize="none"
            rows="4"
            :disabled="locked('files')"
            @change="fileNameChange(inputForm.files[index])"></el-input>
        </div>
      </el-col>
    </el-row>

    <el-row>
      <el-col :md="12">
        <el-form-item :label="$t('콜게이트 전화')">
          <el-input
            v-keep-number-caret
            :value="util.telFieldText(inputForm.callgateTel)"
            maxlength="20"
            clearable
            show-word-limit
            :disabled="locked('callgateTel')"
            @input="inputForm.callgateTel = util.telDigits($event)"></el-input>
        </el-form-item>
      </el-col>
      <el-col :md="12">
        <el-form-item :label="$t('사용 가능 여부')" required>
          <combo
            :data="[
              { label: this.$t('사용 가능'), value: 'Y' },
              { label: this.$t('사용 불가능'), value: 'N' },
            ]"
            :model="inputForm.useYn"
            :disabled="locked('useYn')"
            @change="inputForm.useYn = $event"></combo>
        </el-form-item>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { util } from '@/mixins/utilMixin'
import { mainVueAxios } from '@/plugins/http'
import { pickAddress } from '@/utils/addressPicker'
import { adminRoleMixin } from '@/mixins/adminRoleMixin'
import { isStoreFieldLocked, isStoreFieldWithheld } from '@/utils/storeOps'
import { PG_KEY_TEXT, PG_KIND_OPTIONS, pgKeysDirty, pgKindOf, pgSelection } from '@/utils/storePgKeys'
import CeoVerificationLine from '@/components/common/CeoVerificationLine.vue'

/**
 * 사업자정보 탭 — 업태·종목·이메일·팩스·정산은행·정산계좌·예금주·첨부는 **오더엔 내부 관리자**의 칸이다.
 *
 * 종전에는 `authId === 'admin'` 정확 일치라, 같은 사업장 트리에서 `mng` 이 등록한 매장은 그 값들이
 * 전부 빈 채로 만들어졌다(서버 `POST/PUT /store/mng` 는 두 역할에 열려 있고 필드 단위 가드가 없다).
 *
 * **정산 계좌 셋(은행·계좌번호·예금주)만 더 좁다** — 돈이 실제로 가는 곳이라 서버에 그 축의 액션이
 * 생기기 전까지 `admin` 만 고친다(`canEditSettlementAccount`). 정산은행 코드 조회도 그 칸을 그리는
 * 역할만 부른다. 두 판정 모두 [adminRoleMixin] 한 곳이고 화면은 역할 문자열을 비교하지 않는다.
 *
 * **관리점·운영사의 대행(R-E2)** — 이 탭의 칸은 거의 전부 대행 계정이 바꾸지 못하는 칸이다(사업자 정보·정산 계좌·
 * PG/MID·사용 여부). 서버가 상세에 실은 `lockedFields` 대로 읽기 전용으로 그리고, 서버가 내리지 않은 칸(PG 키·
 * 취소 비밀번호·계좌 번호)은 그리지 않는다 — 판정은 `utils/storeOps` 한 곳이다. 저장은 서버가 강제한다.
 *
 * **PG사별 MID·라이센스 키** — PG사를 바꾸면 그 PG사의 저장값(없으면 빈칸)을 채우고, 미사용이면 두 칸을 잠근다. 저장값 장부와
 * 판정은 `utils/storePgKeys` 한 곳이고, 저장은 고른 PG사 하나다(내려놓는 PG사의 값은 서버가 보관한다).
 */
export default {
  name: 'BusinessInfoFormPanel',

  components: { CeoVerificationLine },

  mixins: [adminRoleMixin],

  props: ['value', 'formError'],

  data() {
    return {
      bankTp: [],
      pgKindOptions: PG_KIND_OPTIONS,
    }
  },

  computed: {
    inputForm: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('update', value)
      },
    },
    /** 고른 PG사 — PG 설정 이력이 없는 매장(`null`)도 미사용으로 그린다 */
    pgKind() {
      return pgKindOf(this.inputForm.pgKind)
    },
    /** 미사용 — MID·라이센스 키를 잠근다 */
    pgUnused() {
      return !this.pgKind
    },
    /**
     * MID 칸 — 미사용이면 빈칸으로 그리고 폼 값은 건드리지 않는다. 남은 값은 어느 PG사의 값도 아니라 보이지 않지만, 저장 본문에는
     * 읽은 그대로 실린다(서버는 미사용 저장에서 두 칸을 바꾸지 않는다 — `utils/storePgKeys` 머리말).
     */
    pgMid: {
      get() {
        return this.pgUnused ? '' : this.inputForm.mid
      },
      set(value) {
        this.inputForm.mid = value
      },
    },
    /** 라이센스 키 칸 — [pgMid] 와 같은 규칙 */
    pgMerchantKey: {
      get() {
        return this.pgUnused ? '' : this.inputForm.merchantKey
      },
      set(value) {
        this.inputForm.merchantKey = value
      },
    },
  },
  mounted() {
    // 정산은행 셀렉트를 그리는 역할만 코드를 읽는다 — 칸이 없는 화면은 부르지 않는다
    if (this.canEditSettlementAccount) {
      this.initBankTp()
    }
  },
  methods: {
    /** 대행 계정이 바꾸지 못하는 칸인가 — 읽기 전용 */
    locked(key) {
      return isStoreFieldLocked(this.inputForm, key)
    },
    /** 잠겼고 서버가 값을 내리지 않은 칸인가 — 그리지 않는다 */
    withheld(key) {
      return isStoreFieldWithheld(this.inputForm, key)
    },
    /**
     * PG사를 바꾼다 — 그 PG사의 저장값으로 MID·라이센스 키를 채운다. 고른 PG사의 입력을 저장하지 않았으면 사라진다고 묻고,
     * 「계속 수정」이면 아무것도 바꾸지 않는다(셀렉트는 폼 값을 그리므로 그대로 남는다).
     */
    choosePgKind(next) {
      const apply = () => Object.assign(this.inputForm, pgSelection(this.inputForm.pgKeyBook, next))

      if (!pgKeysDirty(this.inputForm, this.inputForm.pgKeyBook)) {
        apply()

        return
      }

      this.$confirm(this.$t(PG_KEY_TEXT.discardMessage), this.$t(PG_KEY_TEXT.discardTitle), {
        confirmButtonText: this.$t(PG_KEY_TEXT.discardConfirm),
        cancelButtonText: this.$t(PG_KEY_TEXT.discardCancel),
        type: 'warning',
      })
        .then(apply)
        .catch(() => {})
    },
    initBankTp: function () {
      mainVueAxios.search(
        '/code/getCode/BANK_TP',
        null,
        function (result) {
          this.bankTp = result
        }.bind(this)
      )
    },
    fileNameChange: function (file) {
      if ('ready' != file.status) {
        file.status = 'change'
      }
    },
    storeFileAdd: function (file, fileList) {
      util.imgToBase64(
        URL.createObjectURL(file.raw),
        file.raw.type,
        function (data) {
          this.inputForm.files.push({
            url: data,
            name: file.name,
            status: 'ready',
          })
        }.bind(this)
      )
    },
    storeFileRemove: function (file, fileList) {
      var tmp = -1
      for (var i = 0, len = this.inputForm.files.length; i < len; i++) {
        if (this.inputForm.files[i].url == file.url) {
          tmp = i
        }
      }

      if (tmp > -1) {
        this.inputForm.files.splice(tmp, 1)
      }

      if (file.status == 'success') {
        file.status = 'del'
        this.inputForm.delFiles.push(file)
      }
    },
    /**
     * 주소 선택 — 위젯은 `utils/addressPicker` 한 곳이 만진다(종전 `initDaum` 다섯 사본을 접었다).
     * 채우는 필드(우편번호·주소)와 포커스 이동은 종전 그대로다.
     */
    selectAddress: async function () {
      let picked = null
      try {
        picked = await pickAddress({ geocode: false })
      } catch (error) {
        this.$message({ type: 'error', message: error.message })

        return
      }
      if (!picked) return

      this.inputForm.zipCd = picked.zonecode
      this.inputForm.addr = picked.address

      this.$refs.addr_dtl.focus()
    },
  },
}
</script>

<style scoped>
.el-col {
  padding: 4px;
}
</style>
